/* eslint-disable no-undef */
import { AxiosInstance, CancelToken } from 'axios';
import { CustomMetadata } from 'modules/footprints/airFreightSlice';
import { createContextAwareHandlers } from './instance';

interface BatchPayload extends CustomMetadata {
  emissionName: string;
  description: string;
  storageBlobName: string;
}

interface BatchResponseOk {
  jobId: string;
  partnershipId: string;
}

interface BatchResponseError {
  message: string;
  data: string[];
}

const createBatchOrderForPartner = (instance: AxiosInstance) => async (
  payload: BatchPayload,
  sourceToken: CancelToken,
  partnershipId: string,
): Promise<BatchResponseOk & BatchResponseError> => {
  const { data } = await instance.post<BatchResponseOk & BatchResponseError>(
    `/partnerships/${partnershipId}/footprints/multiplecustomerairfreightbatch`,
    payload,
    {
      cancelToken: sourceToken,
    },
  );

  return data;
};

const createBatchOrderForCustomer = (instance: AxiosInstance) => async (
  payload: BatchPayload,
  sourceToken: CancelToken,
  customerId: string,
): Promise<BatchResponseOk & BatchResponseError> => {
  const { data } = await instance.post<BatchResponseOk & BatchResponseError>(`/customers/${customerId}/footprints/airfreightbatch`, payload, {
    cancelToken: sourceToken,
  });

  return data;
};

const uploadFootprintBatchForCustomer = (instance: AxiosInstance) => async (
  file: File,
  sourceToken: CancelToken,
  customerId: string,
): Promise<BatchResponseOk & BatchResponseError> => {
  const { data } = await instance.post<BatchResponseOk & BatchResponseError>(`/customers/${customerId}/attachments/footprintairfreightbatch`, file, {
    headers: {
      'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
    cancelToken: sourceToken,
  });

  return data;
};

const uploadFootprintBatchForPartner = (instance: AxiosInstance) => async (
  file: File,
  sourceToken: CancelToken,
  customerId: string,
): Promise<BatchResponseOk & BatchResponseError> => {
  const { data } = await instance.post<BatchResponseOk & BatchResponseError>(
    `/customers/${customerId}/attachments/footprintmultiplecustomerairfreightbatch`,
    file,
    {
      headers: {
        'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
      cancelToken: sourceToken,
    },
  );

  return data;
};

export const getJobStatus = (instance: AxiosInstance) => async <T>(
  partnershipId: string,
  jobId: string,
  sourceToken: CancelToken,
): Promise<JobStatusResponse<T>> => {
  const { data } = await instance.get<JobStatusResponse<T>>(`/partnerships/${partnershipId}/jobs/${jobId}`, { cancelToken: sourceToken });
  return data;
};

const handlers = {
  createBatchOrderForPartner,
  createBatchOrderForCustomer,
  getJobStatus,
  uploadFootprintBatchForCustomer,
  uploadFootprintBatchForPartner,
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const createFootprintAirFreightApi = (context: PortalContext) => {
  return createContextAwareHandlers(context, handlers);
};

export default createFootprintAirFreightApi;
